import { memoize } from "lodash";

/**
 * Get a thumbnail URL for YouTube, Vimeo or Bunny.net
 * @param {string} url - YouTube, Vimeo or Bunny.net video URL
 * @returns {Promise<string|null>} thumbnail URL or null
 */
export const getVideoThumbnail = memoize(async function getVideoThumbnail(url) {
    const youtubeId = extractYouTubeId(url);
    if (youtubeId) {
        return getYouTubeThumbnail(youtubeId);
    }

    const vimeoId = extractVimeoId(url);
    if (vimeoId) {
        return await getVimeoThumbnail(url);
    }

    if (isBunnyUrl(url)) {
        return await getBunnyThumbnail(url);
    }

    return null;
});

/* ------------------ YouTube ------------------ */

function extractYouTubeId(url) {
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([^?&/]+)/i);
    return match?.[1] ?? null;
}

function getYouTubeThumbnail(videoId) {
    // Try max resolution first; YouTube will fall back automatically if unavailable
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

/* ------------------ Vimeo ------------------ */

function extractVimeoId(url) {
    const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    return match?.[1] ?? null;
}

async function getVimeoThumbnail(url) {
    try {
        const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`);

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        return data.thumbnail_url ?? null;
    } catch {
        return null;
    }
}

/* ------------------ Bunny.net ------------------ */

function isBunnyUrl(url) {
    return /(?:iframe|player)\.mediadelivery\.net\/(?:embed|play)\/\d+\/[\w-]+/i.test(url);
}

async function getBunnyThumbnail(url) {
    try {
        const response = await fetch(`https://video.bunnycdn.com/OEmbed?url=${encodeURIComponent(url)}`);

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        return data.thumbnail_url ?? null;
    } catch {
        return null;
    }
}
